// Budget demo · seed data.
//
// seedWorkspace() builds the in-memory "database": ~28 hand-authored material
// contracts (the individually-modeled top of the book), a deterministically
// generated long tail, a set of FY2027 new-spend initiatives, and the two
// budgets — FY2026 (Tracking, with actuals posted) and FY2027 (Draft). The
// budget LINES are produced by the same forecast.ts builders the engine uses on
// ingest, so the seed and live recompute can never diverge.

import {
    buildBudgetLine,
    buildInitiativeLine,
    buildNewContractLine,
    buildProjectLine,
    hashSeed,
    InitiativeSpec,
    mulberry32,
} from "./forecast";
import {
    Budget,
    BudgetLine,
    CapBasis,
    Category,
    categoryId,
    CategoryKey,
    Contract,
    contractId,
    DataSource,
    PricingType,
    sourceId,
    usd,
    Vendor,
    vendorId,
    VendorGrade,
    Workspace,
} from "./types";
import { WPC_PROJECTS } from "./projects";

// ---------------------------------------------------------------------------
// Demo constants
// ---------------------------------------------------------------------------
export const CURRENT_YEAR = 2026;
/** The fake demo budget is scaled up so the real linked projects (kept at their
 * true production size) sit naturally alongside it. Real project numbers in
 * projects.ts are NOT scaled by this. */
export const FAKE_SCALE = 7;
/** Contracts below this are pooled as tail regardless of status (spec §4). */
export const MATERIALITY = usd(150_000 * FAKE_SCALE);
/** Q1–Q2 posted as of June (today is 2026-06-23). */
export const ELAPSED_QUARTERS = 2;

// ---------------------------------------------------------------------------
// Categories
// ---------------------------------------------------------------------------
const CATEGORY_DEFS: Array<{ key: CategoryKey; name: string }> = [
    { key: "saas", name: "SaaS & Applications" },
    { key: "infra_cloud", name: "Cloud Infrastructure" },
    { key: "hardware", name: "Hardware & Networking" },
    { key: "telecom", name: "Telecom & Connectivity" },
    { key: "prof_services", name: "Professional Services" },
    { key: "security", name: "Security" },
    { key: "data_analytics", name: "Data & Analytics" },
    { key: "support_maint", name: "Support & Maintenance" },
];

const CATEGORIES: Category[] = CATEGORY_DEFS.map((c) => ({
    id: categoryId(`cat-${c.key}`),
    key: c.key,
    name: c.name,
}));

const catByKey = (key: CategoryKey): Category =>
    CATEGORIES.find((c) => c.key === key)!;

// ---------------------------------------------------------------------------
// Material contracts — hand-authored top of the book
// ---------------------------------------------------------------------------
interface ContractSeed {
    name: string;
    cat: CategoryKey;
    spend: number;
    start: string;
    end: string;
    renewal: string;
    autoRenew: boolean;
    notice: number;
    capped: boolean;
    capBasis: CapBasis;
    pricing: PricingType;
    escClause: string;
    escPct: number;
    grade?: VendorGrade;
}

const MATERIAL_SEEDS: ContractSeed[] = [
    // SaaS & Applications
    { name: "Microsoft 365 E5", cat: "saas", spend: 3_200_000, start: "Jul 1, 2022", end: "Jun 30, 2027", renewal: "Jun 30, 2027", autoRenew: false, notice: 180, capped: false, capBasis: "none", pricing: "seat_based", escClause: "no cap — list +12% notified", escPct: 0.12, grade: "B" },
    { name: "Salesforce CRM", cat: "saas", spend: 1_700_000, start: "Oct 1, 2023", end: "Sep 30, 2027", renewal: "Sep 30, 2027", autoRenew: true, notice: 150, capped: false, capBasis: "none", pricing: "seat_based", escClause: "uncapped at renewal", escPct: 0.1, grade: "B-" },
    { name: "Workday HCM", cat: "saas", spend: 950_000, start: "Jan 1, 2023", end: "Dec 31, 2026", renewal: "Dec 31, 2026", autoRenew: true, notice: 120, capped: true, capBasis: "CPI", pricing: "seat_based", escClause: "CPI, capped 4%", escPct: 0.04, grade: "B+" },
    { name: "ServiceNow ITSM", cat: "saas", spend: 1_100_000, start: "Apr 1, 2024", end: "Mar 31, 2027", renewal: "Mar 31, 2027", autoRenew: false, notice: 90, capped: false, capBasis: "none", pricing: "seat_based", escClause: "uncapped", escPct: 0.11, grade: "B" },
    { name: "Atlassian Cloud", cat: "saas", spend: 360_000, start: "Aug 1, 2023", end: "Jul 31, 2027", renewal: "Jul 31, 2027", autoRenew: true, notice: 60, capped: true, capBasis: "fixed_pct", pricing: "seat_based", escClause: "fixed 5%/yr", escPct: 0.05, grade: "B" },
    { name: "DocuSign CLM", cat: "saas", spend: 210_000, start: "Mar 1, 2024", end: "Feb 28, 2028", renewal: "Feb 28, 2028", autoRenew: true, notice: 45, capped: true, capBasis: "fixed_pct", pricing: "seat_based", escClause: "fixed 4%/yr", escPct: 0.04, grade: "B+" },

    // Cloud Infrastructure
    { name: "Microsoft Azure", cat: "infra_cloud", spend: 1_000_000, start: "Jul 1, 2023", end: "Jun 30, 2027", renewal: "Jun 30, 2027", autoRenew: false, notice: 90, capped: false, capBasis: "none", pricing: "usage", escClause: "MACC — usage-driven", escPct: 0.1, grade: "B" },
    { name: "Google Cloud Platform", cat: "infra_cloud", spend: 720_000, start: "May 1, 2024", end: "Apr 30, 2028", renewal: "Apr 30, 2028", autoRenew: false, notice: 60, capped: false, capBasis: "none", pricing: "usage", escClause: "committed-use, no cap", escPct: 0.07, grade: "B" },
    { name: "Cloudflare Enterprise", cat: "infra_cloud", spend: 240_000, start: "Sep 1, 2024", end: "Aug 31, 2026", renewal: "Aug 31, 2026", autoRenew: true, notice: 60, capped: true, capBasis: "CPI", pricing: "fixed", escClause: "CPI, capped 5%", escPct: 0.05, grade: "A-" },

    // Hardware & Networking
    { name: "Dell Technologies", cat: "hardware", spend: 850_000, start: "Jan 1, 2024", end: "Dec 31, 2028", renewal: "Dec 31, 2028", autoRenew: false, notice: 90, capped: true, capBasis: "step", pricing: "fixed", escClause: "step schedule, fixed", escPct: 0.02, grade: "B+" },
    { name: "Cisco Networking", cat: "hardware", spend: 720_000, start: "Jul 1, 2023", end: "Jun 30, 2027", renewal: "Jun 30, 2027", autoRenew: false, notice: 120, capped: true, capBasis: "CPI", pricing: "fixed", escClause: "CPI, capped 4%", escPct: 0.04, grade: "B" },
    { name: "CDW Hardware Supply", cat: "hardware", spend: 520_000, start: "Jan 1, 2024", end: "Dec 31, 2026", renewal: "Dec 31, 2026", autoRenew: false, notice: 30, capped: false, capBasis: "none", pricing: "fixed", escClause: "reseller margin, no cap", escPct: 0.06, grade: "C+" },

    // Telecom & Connectivity
    { name: "AT&T Connectivity", cat: "telecom", spend: 620_000, start: "Mar 1, 2023", end: "Feb 28, 2027", renewal: "Feb 28, 2027", autoRenew: true, notice: 120, capped: true, capBasis: "fixed_pct", pricing: "fixed", escClause: "fixed 2%/yr", escPct: 0.02, grade: "B" },
    { name: "Verizon Mobile Fleet", cat: "telecom", spend: 410_000, start: "Jun 1, 2024", end: "May 31, 2026", renewal: "May 31, 2026", autoRenew: true, notice: 60, capped: true, capBasis: "CPI", pricing: "usage", escClause: "CPI, capped 3%", escPct: 0.03, grade: "B-" },

    // Professional Services
    { name: "Accenture SI", cat: "prof_services", spend: 1_200_000, start: "Jan 1, 2025", end: "Dec 31, 2026", renewal: "Dec 31, 2026", autoRenew: false, notice: 30, capped: false, capBasis: "none", pricing: "fixed", escClause: "T&M, rate card reset", escPct: 0.08, grade: "C+" },
    { name: "Deloitte Advisory", cat: "prof_services", spend: 700_000, start: "Apr 1, 2024", end: "Mar 31, 2027", renewal: "Mar 31, 2027", autoRenew: false, notice: 30, capped: false, capBasis: "none", pricing: "fixed", escClause: "rate card, no cap", escPct: 0.07, grade: "C" },

    // Security
    { name: "CrowdStrike Falcon", cat: "security", spend: 620_000, start: "Aug 1, 2024", end: "Jul 31, 2027", renewal: "Jul 31, 2027", autoRenew: true, notice: 60, capped: true, capBasis: "fixed_pct", pricing: "seat_based", escClause: "fixed 5%/yr", escPct: 0.05, grade: "A-" },
    { name: "Palo Alto Networks", cat: "security", spend: 540_000, start: "Feb 1, 2024", end: "Jan 31, 2028", renewal: "Jan 31, 2028", autoRenew: false, notice: 90, capped: true, capBasis: "CPI", pricing: "fixed", escClause: "CPI, capped 4%", escPct: 0.04, grade: "B+" },
    { name: "Okta Identity", cat: "security", spend: 430_000, start: "Nov 1, 2023", end: "Oct 31, 2026", renewal: "Oct 31, 2026", autoRenew: true, notice: 60, capped: true, capBasis: "fixed_pct", pricing: "seat_based", escClause: "fixed 6%/yr", escPct: 0.06, grade: "B" },

    // Data & Analytics
    { name: "Snowflake", cat: "data_analytics", spend: 780_000, start: "Apr 1, 2024", end: "Mar 31, 2027", renewal: "Mar 31, 2027", autoRenew: false, notice: 90, capped: false, capBasis: "none", pricing: "usage", escClause: "consumption, no cap", escPct: 0.13, grade: "B-" },
    { name: "Databricks", cat: "data_analytics", spend: 560_000, start: "Jul 1, 2024", end: "Jun 30, 2027", renewal: "Jun 30, 2027", autoRenew: false, notice: 60, capped: false, capBasis: "none", pricing: "usage", escClause: "consumption, no cap", escPct: 0.12, grade: "B" },
    { name: "Datadog", cat: "data_analytics", spend: 480_000, start: "Sep 1, 2024", end: "Aug 31, 2026", renewal: "Aug 31, 2026", autoRenew: true, notice: 45, capped: true, capBasis: "CPI", pricing: "usage", escClause: "CPI, capped 5%", escPct: 0.05, grade: "B-" },

    // Support & Maintenance
    { name: "Oracle Support", cat: "support_maint", spend: 1_050_000, start: "Jan 1, 2023", end: "Dec 31, 2027", renewal: "Dec 31, 2027", autoRenew: true, notice: 150, capped: false, capBasis: "none", pricing: "fixed", escClause: "support uplift, no cap", escPct: 0.08, grade: "C-" },
    { name: "Red Hat Subscriptions", cat: "support_maint", spend: 360_000, start: "May 1, 2024", end: "Apr 30, 2028", renewal: "Apr 30, 2028", autoRenew: true, notice: 60, capped: true, capBasis: "fixed_pct", pricing: "fixed", escClause: "fixed 4%/yr", escPct: 0.04, grade: "B" },
    { name: "SAP S/4HANA Licenses", cat: "saas", spend: 1_600_000, start: "Jan 1, 2023", end: "Dec 31, 2028", renewal: "Dec 31, 2028", autoRenew: false, notice: 180, capped: true, capBasis: "fixed_pct", pricing: "fixed", escClause: "fixed 3.5%/yr", escPct: 0.035, grade: "C+" },
];

/** Vendor brand domains — drive the logo.dev logos in the budget table. */
const VENDOR_DOMAINS: Record<string, string> = {
    "Microsoft 365 E5": "microsoft.com",
    "Salesforce CRM": "salesforce.com",
    "Workday HCM": "workday.com",
    "ServiceNow ITSM": "servicenow.com",
    "Adobe Creative Cloud": "adobe.com",
    "Atlassian Cloud": "atlassian.com",
    "DocuSign CLM": "docusign.com",
    "Amazon Web Services": "aws.amazon.com",
    "Microsoft Azure": "azure.microsoft.com",
    "Google Cloud Platform": "cloud.google.com",
    "Cloudflare Enterprise": "cloudflare.com",
    "Dell Technologies": "dell.com",
    "Cisco Networking": "cisco.com",
    "CDW Hardware Supply": "cdw.com",
    "AT&T Connectivity": "att.com",
    "Verizon Mobile Fleet": "verizon.com",
    "Accenture SI": "accenture.com",
    "Deloitte Advisory": "deloitte.com",
    "CrowdStrike Falcon": "crowdstrike.com",
    "Palo Alto Networks": "paloaltonetworks.com",
    "Okta Identity": "okta.com",
    Snowflake: "snowflake.com",
    Databricks: "databricks.com",
    Datadog: "datadoghq.com",
    "Oracle Support": "oracle.com",
    "Red Hat Subscriptions": "redhat.com",
    "SAP S/4HANA Licenses": "sap.com",
    "GitHub Enterprise": "github.com",
    "MongoDB Atlas": "mongodb.com",
    "Zscaler Internet Access": "zscaler.com",
    "Twilio Programmable Comms": "twilio.com",
    "Tableau Analytics": "tableau.com",
    PagerDuty: "pagerduty.com",
    "Lenovo Hardware": "lenovo.com",
    "Confluent Cloud": "confluent.io",
};

let seq = 0;
function seedToContract(s: ContractSeed, idPrefix = "ct"): { contract: Contract; vendor: Vendor } {
    const cat = catByKey(s.cat);
    const cid = contractId(`${idPrefix}-${seq}`);
    const vid = vendorId(`vn-${idPrefix}-${seq}`);
    seq += 1;
    const contract: Contract = {
        id: cid,
        vendorId: vid,
        vendorName: s.name,
        categoryId: cat.id,
        categoryName: cat.name,
        annualSpend: usd(s.spend * FAKE_SCALE),
        startDate: s.start,
        endDate: s.end,
        renewalDate: s.renewal,
        autoRenew: s.autoRenew,
        noticePeriodDays: s.notice,
        increaseCapped: s.capped,
        capBasis: s.capBasis,
        pricingType: s.pricing,
        escalatorClause: s.escClause,
        escalatorPct: s.escPct,
        source: "connector",
        material: s.spend * FAKE_SCALE >= MATERIALITY.amount,
        domain: VENDOR_DOMAINS[s.name],
    };
    const vendor: Vendor = {
        id: vid,
        name: s.name,
        categoryId: cat.id,
        grade: s.grade,
    };
    return { contract, vendor };
}

// ---------------------------------------------------------------------------
// Pending material contracts — surfaced by "Upload contracts"
// ---------------------------------------------------------------------------
const PENDING_SEEDS: ContractSeed[] = [
    { name: "GitHub Enterprise", cat: "saas", spend: 380_000, start: "Jun 1, 2024", end: "May 31, 2027", renewal: "May 31, 2027", autoRenew: true, notice: 60, capped: true, capBasis: "fixed_pct", pricing: "seat_based", escClause: "fixed 5%/yr", escPct: 0.05, grade: "A-" },
    { name: "MongoDB Atlas", cat: "infra_cloud", spend: 460_000, start: "Mar 1, 2024", end: "Feb 28, 2027", renewal: "Feb 28, 2027", autoRenew: false, notice: 60, capped: false, capBasis: "none", pricing: "usage", escClause: "consumption, no cap", escPct: 0.11, grade: "B" },
    { name: "Zscaler Internet Access", cat: "security", spend: 520_000, start: "Jan 1, 2024", end: "Dec 31, 2027", renewal: "Dec 31, 2027", autoRenew: true, notice: 90, capped: true, capBasis: "CPI", pricing: "seat_based", escClause: "CPI, capped 4%", escPct: 0.04, grade: "B+" },
    { name: "Twilio Programmable Comms", cat: "telecom", spend: 290_000, start: "Aug 1, 2024", end: "Jul 31, 2026", renewal: "Jul 31, 2026", autoRenew: true, notice: 30, capped: false, capBasis: "none", pricing: "usage", escClause: "usage-driven, no cap", escPct: 0.09, grade: "B-" },
    { name: "Tableau Analytics", cat: "data_analytics", spend: 340_000, start: "Apr 1, 2024", end: "Mar 31, 2027", renewal: "Mar 31, 2027", autoRenew: true, notice: 60, capped: true, capBasis: "fixed_pct", pricing: "seat_based", escClause: "fixed 4%/yr", escPct: 0.04, grade: "B" },
    { name: "PagerDuty", cat: "saas", spend: 180_000, start: "Sep 1, 2024", end: "Aug 31, 2027", renewal: "Aug 31, 2027", autoRenew: true, notice: 45, capped: true, capBasis: "fixed_pct", pricing: "seat_based", escClause: "fixed 5%/yr", escPct: 0.05, grade: "B+" },
    { name: "Lenovo Hardware", cat: "hardware", spend: 410_000, start: "Jan 1, 2024", end: "Dec 31, 2028", renewal: "Dec 31, 2028", autoRenew: false, notice: 60, capped: true, capBasis: "step", pricing: "fixed", escClause: "step schedule, fixed", escPct: 0.02, grade: "B" },
    { name: "Confluent Cloud", cat: "data_analytics", spend: 300_000, start: "Jul 1, 2024", end: "Jun 30, 2027", renewal: "Jun 30, 2027", autoRenew: false, notice: 60, capped: false, capBasis: "none", pricing: "usage", escClause: "consumption, no cap", escPct: 0.12, grade: "B-" },
];

// ---------------------------------------------------------------------------
// FY2027 new-spend initiatives (spec §5 — the relief valve)
// ---------------------------------------------------------------------------
const INITIATIVE_DEFS: Array<Omit<InitiativeSpec, "categoryId" | "categoryName">> = [
    { key: "ai-platform", name: "AI platform pilot (LLM tooling)", categoryKey: "data_analytics", expected: 600_000, low: 350_000, high: 1_100_000, quarter: 2, rationale: "Discretionary — board-sponsored GenAI pilot; pressure-tested, staged spend", owner: "CIO office" },
    { key: "sase", name: "SASE network rollout", categoryKey: "security", expected: 420_000, low: 300_000, high: 650_000, quarter: 3, rationale: "Driver: branch consolidation; trend allowance pre-filled from vendor quotes", owner: "Security · architecture" },
    { key: "dc-exit", name: "Data-center exit (cloud migration)", categoryKey: "infra_cloud", expected: 540_000, low: 380_000, high: 900_000, quarter: 4, rationale: "Named pipeline — phased lift-and-shift; discretionary reserve carried", owner: "Infrastructure" },
];

const INITIATIVES: InitiativeSpec[] = INITIATIVE_DEFS.map((d) => {
    const cat = catByKey(d.categoryKey as CategoryKey);
    return {
        ...d,
        expected: d.expected * FAKE_SCALE,
        low: d.low * FAKE_SCALE,
        high: d.high * FAKE_SCALE,
        categoryId: cat.id,
        categoryName: cat.name,
    };
});

// ---------------------------------------------------------------------------
// Newly-signed contracts — new spend (not renewals). No increase; some carry a
// savings playbook to negotiate the new contract down.
// ---------------------------------------------------------------------------
interface NewContractDef {
    name: string;
    cat: CategoryKey;
    spend: number;
    domain: string;
    playbook: boolean;
}

const NEW_CONTRACT_DEFS: NewContractDef[] = [
    { name: "Vercel Enterprise", cat: "infra_cloud", spend: 280_000, domain: "vercel.com", playbook: true },
    { name: "Ramp", cat: "saas", spend: 190_000, domain: "ramp.com", playbook: true },
    { name: "Rippling HRIS", cat: "saas", spend: 240_000, domain: "rippling.com", playbook: true },
    { name: "Notion Enterprise", cat: "saas", spend: 120_000, domain: "notion.so", playbook: false },
];

const NEW_CONTRACTS: Array<{ contract: Contract; playbook: boolean }> =
    NEW_CONTRACT_DEFS.map((d, i) => {
        const cat = catByKey(d.cat);
        const contract: Contract = {
            id: contractId(`nc-${i}`),
            vendorId: vendorId(`vn-nc-${i}`),
            vendorName: d.name,
            categoryId: cat.id,
            categoryName: cat.name,
            annualSpend: usd(d.spend * FAKE_SCALE),
            startDate: "Jan 1, 2027",
            endDate: "Dec 31, 2029",
            renewalDate: "Dec 31, 2029",
            autoRenew: false,
            noticePeriodDays: 60,
            increaseCapped: false,
            capBasis: "none",
            pricingType: "usage",
            escalatorClause: "n/a",
            escalatorPct: 0,
            source: "manual",
            material: d.spend * FAKE_SCALE >= MATERIALITY.amount,
            domain: d.domain,
        };
        return { contract, playbook: d.playbook };
    });

// ---------------------------------------------------------------------------
// Tail generator — deterministic; reused by the engine for the PO feed.
// ---------------------------------------------------------------------------
const TAIL_NAMES = [
    "Iron Mountain", "Shutterstock", "Grammarly", "Calendly", "Miro", "Notion",
    "1Password", "Lucidchart", "Twilio SendGrid", "Mailchimp", "Asana",
    "Monday.com", "Smartsheet", "Box", "Dropbox Business", "Zapier", "Airtable",
    "Loom", "Figma", "Intercom", "Zendesk", "Freshworks", "HubSpot", "Segment",
    "Mixpanel", "Amplitude", "LogRocket", "Sentry", "New Relic", "Sumo Logic",
    "Fastly", "Akamai", "Twingate", "JumpCloud", "Rippling", "Greenhouse",
    "Lever", "Gusto", "Carta", "SmartRecruiters", "Webflow", "Vimeo Enterprise",
    "Bitwarden", "Postman", "Linear",
];

const TAIL_CATEGORY_KEYS: CategoryKey[] = [
    "saas", "saas", "saas", "data_analytics", "security", "infra_cloud",
    "support_maint", "telecom", "hardware",
];

export function generateTailContracts(
    seedKey: string,
    count: number,
    idPrefix: string
): { contracts: Contract[]; vendors: Vendor[] } {
    const rng = mulberry32(hashSeed(seedKey));
    const contracts: Contract[] = [];
    const vendors: Vendor[] = [];
    for (let i = 0; i < count; i++) {
        const name = `${TAIL_NAMES[Math.floor(rng() * TAIL_NAMES.length)]}`;
        const catKey = TAIL_CATEGORY_KEYS[Math.floor(rng() * TAIL_CATEGORY_KEYS.length)];
        const cat = catByKey(catKey);
        const spend = Math.round(8_000 + rng() * 88_000); // individual tail POs, < $100K
        const cid = contractId(`${idPrefix}-${i}`);
        const vid = vendorId(`vn-${idPrefix}-${i}`);
        contracts.push({
            id: cid,
            vendorId: vid,
            vendorName: name,
            categoryId: cat.id,
            categoryName: cat.name,
            annualSpend: usd(spend),
            startDate: "2024",
            endDate: "Dec 31, 2027",
            renewalDate: "rolling",
            autoRenew: true,
            noticePeriodDays: 30,
            increaseCapped: false,
            capBasis: "none",
            pricingType: "usage",
            escalatorClause: "off-contract / rolling",
            escalatorPct: 0.05,
            source: "connector",
            material: false,
        });
        vendors.push({ id: vid, name, categoryId: cat.id });
    }
    return { contracts, vendors };
}

// ---------------------------------------------------------------------------
// Budget assembly
// ---------------------------------------------------------------------------
/** Actuals are posted for closed years (full year) and the current year (H1);
 * future years carry budget only. */
export function optsForYear(year: number): {
    withActuals: boolean;
    elapsedQuarters: number;
} {
    return {
        withActuals: year <= CURRENT_YEAR,
        elapsedQuarters: year < CURRENT_YEAR ? 4 : ELAPSED_QUARTERS,
    };
}

/** Material contracts (point / range) + (current year and beyond) new-spend
 * initiatives. Tail spend is pooled live in the view from the PO pool, so it's
 * not a budget line here. */
export function buildYearLines(
    contracts: Contract[],
    year: number
): BudgetLine[] {
    const opts = optsForYear(year);
    const material = contracts
        .filter((c) => c.material)
        .map((c) => buildBudgetLine(c, year, MATERIALITY, opts))
        .filter((l): l is BudgetLine => l !== null);
    const initiatives =
        year >= CURRENT_YEAR
            ? INITIATIVES.map((spec) => buildInitiativeLine(spec, year))
            : [];
    const newContracts =
        year >= CURRENT_YEAR
            ? NEW_CONTRACTS.map(({ contract, playbook }) =>
                  buildNewContractLine(contract, year, playbook)
              )
            : [];
    // Real negotiation projects (WPC Demo) — current FY and beyond, so they show
    // in both the Budget and Track tabs (which share the fiscal year).
    const projects =
        year >= CURRENT_YEAR
            ? WPC_PROJECTS.map((p) => {
                  const cat = catByKey(p.categoryKey ?? "saas");
                  return buildProjectLine(p, year, cat.id, p.category);
              })
            : [];
    return [...material, ...initiatives, ...newContracts, ...projects];
}

// ---------------------------------------------------------------------------
// Sources — each reflects the records already imported / uploaded into the
// system (the Sources tab is a read-only window onto that evidence).
// ---------------------------------------------------------------------------
const sumSpend = (cs: Contract[]) =>
    usd(cs.reduce((a, c) => a + c.annualSpend.amount, 0));

function seedSources(
    materialContracts: Contract[],
    tailContracts: Contract[],
    uploadedContracts: Contract[]
): DataSource[] {
    return [
        {
            id: sourceId("src-projects"),
            kind: "project",
            system: "Negotiation projects",
            name: "Projects",
            detail: "Linked negotiation projects · live deals",
            live: true,
            status: "connected",
            logoLabel: "PR",
            logoColor: "#7c3aed",
            lastSyncedLabel: "Live",
            contractsIngested: WPC_PROJECTS.length,
            spendDollarsCovered: usd(
                WPC_PROJECTS.reduce((a, p) => a + p.annual.amount, 0)
            ),
        },
        {
            id: sourceId("src-ariba"),
            kind: "contract_upload",
            system: "SAP Ariba",
            name: "SAP Ariba — Contracts",
            detail: "Committed agreements · contract repository",
            live: true,
            status: "connected",
            logoLabel: "AR",
            logoColor: "#0fa0ce",
            lastSyncedLabel: "Today · 06:40",
            contractsIngested: materialContracts.length,
            spendDollarsCovered: sumSpend(materialContracts),
        },
        {
            id: sourceId("src-po-feed"),
            kind: "po_feed",
            system: "S/4HANA",
            name: "S/4HANA — PO feed",
            detail: "Purchase orders · tail-spend pool",
            live: true,
            status: "connected",
            logoLabel: "S4",
            logoColor: "#1f6fb2",
            lastSyncedLabel: "Today · 06:40",
            contractsIngested: tailContracts.length,
            spendDollarsCovered: sumSpend(tailContracts),
        },
    ];
}

// ---------------------------------------------------------------------------
// seedWorkspace
// ---------------------------------------------------------------------------
export function seedWorkspace(): Workspace {
    seq = 0;
    const built = MATERIAL_SEEDS.map((s) => seedToContract(s));
    const materialContracts = built.map((b) => b.contract);
    const materialVendors = built.map((b) => b.vendor);

    // Baseline tail already pulled from the contract repository.
    const baselineTail = generateTailContracts("budget-tail-baseline", 110, "tl");

    const contracts = [...materialContracts, ...baselineTail.contracts];
    const vendors = [...materialVendors, ...baselineTail.vendors];

    const uploadedContracts = PENDING_SEEDS.map((s) => seedToContract(s, "pend").contract);

    // Current year, one forward year, plus two closed years for YoY baselines.
    const years = [-2, -1, 0, 1].map((d) => CURRENT_YEAR + d);
    const budgets: Budget[] = years.map((year) => ({
        year,
        state: year > CURRENT_YEAR ? { kind: "Draft" } : { kind: "Tracking" },
        lines: buildYearLines(contracts, year),
    }));

    return {
        org: { id: "org-demo", name: "", unit: "IT Procurement" },
        currentUser: { id: "u-1", name: "Dana Whitfield", role: "Procurement Lead" },
        currentYear: CURRENT_YEAR,
        materialityThreshold: MATERIALITY,
        categories: CATEGORIES,
        vendors,
        contracts,
        budgets,
        sources: seedSources(
            materialContracts,
            baselineTail.contracts,
            uploadedContracts
        ),
        uploadedContracts,
    };
}
